<template>
  <div class="invoice-form">
    <fieldset :disabled="isReadOnly" class="invoice-fieldset">
      <b-row>
        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>{{ $t('invoiceDetails') }}</strong>
          </legend>

          <b-form-group
            horizontal
            :label="$t('invoiceType')"
            label-for="invoiceType"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <multiselect
              :disabled="isReadOnly"
              :placeholder="$t('invoiceType') + ' ' + $t('select')"
              v-model="localInvoice.invoiceType"
              :options="getEnumValues('InvoiceType')"
              :show-labels="true"
              :allow-empty="false"
              :taggable="true"
              :selectLabel="$t('select')"
              :selectedLabel="$t('selected')"
              :deselectLabel="$t('remove_selection')"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                <b-badge :variant="getInvoiceTypeColor(option)">{{ $t(option) }}</b-badge>
              </template>
              <template slot="option" slot-scope="{ option }">
                <b-badge :variant="getInvoiceTypeColor(option)">
                  {{ $t(option) }}
                </b-badge>
              </template>
            </multiselect>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('invoiceState')"
            label-for="invoiceState"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <multiselect
              :disabled="isReadOnly"
              :placeholder="$t('invoiceState') + ' ' + $t('select')"
              v-model="localInvoice.invoiceState"
              :options="getEnumValues('InvoiceState')"
              :show-labels="true"
              :allow-empty="false"
              :taggable="true"
              :selectLabel="$t('select')"
              :selectedLabel="$t('selected')"
              :deselectLabel="$t('remove_selection')"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                <b-badge :variant="getInvoiceStateColor(option)">{{ $t(option) }}</b-badge>
              </template>
              <template slot="option" slot-scope="{ option }">
                <b-badge :variant="getInvoiceStateColor(option)">
                  {{ $t(option) }}
                </b-badge>
              </template>
            </multiselect>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('invoiceNumber')"
            label-for="invoiceNumber"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input
              v-model="localInvoice.invoiceNumber"
              type="text"
              :placeholder="$t('invoiceNumber')"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('invoiceDate')"
            label-for="invoiceDate"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <Calendar
              v-model="invoiceDate"
              :disabled="isReadOnly"
              selectionMode="single"
              dateFormat="dd.mm.yy"
              :placeholder="$t('date')"
              :showWeek="true"
              :yearNavigator="true"
              :monthNavigator="true"
              yearRange="2020:2030"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('dueDate')"
            label-for="dueDate"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <Calendar
              v-model="dueDate"
              :disabled="isReadOnly"
              selectionMode="single"
              dateFormat="dd.mm.yy"
              :placeholder="$t('date')"
              :showWeek="true"
              :yearNavigator="true"
              :monthNavigator="true"
              yearRange="2020:2030"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('paidDate')"
            label-for="paidDate"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <Calendar
              v-model="paidDate"
              :disabled="isReadOnly"
              selectionMode="single"
              dateFormat="dd.mm.yy"
              :placeholder="$t('date')"
              :showWeek="true"
              :yearNavigator="true"
              :monthNavigator="true"
              yearRange="2020:2030"
            />
          </b-form-group>
        </b-col>

        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>{{ $t('amounts') }}</strong>
          </legend>

          <TaxAmountFields
            :amounts="invoiceAmounts"
            :isReadOnly="isReadOnly"
            :calculate="calculateInvoiceAmounts"
            @update:amounts="onAmountsChange"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>{{ $t('references') }}</strong>
          </legend>

          <b-form-group
            horizontal
            :label="$t('supplier')"
            label-for="supplier"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <MultiselectSuppliers v-model="localInvoice.supplier" :disabled="isReadOnly" />
          </b-form-group>

          <b-form-group
            v-if="!projectContext"
            horizontal
            :label="$t('project')"
            label-for="project"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <multiselect
              :disabled="isReadOnly"
              class="multiselect-project"
              :placeholder="$t('filter_by_lastname')"
              v-model="localInvoice.project"
              :options="filteredProjects"
              :allow-empty="true"
              track-by="number"
              :deselectLabel="$t('deselectLabel')"
              selectLabel=""
              selectedLabel=""
              :internal-search="false"
              :show-labels="true"
              @search-change="onProjectSearch"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                <div>
                  #{{ option.number.toString().padStart(4, '0') }} - {{ option.customer.lastname }},
                  {{ option.customer.firstname }}
                </div>
                <div>
                  {{ option.customer.street }} {{ option.customer.streetNumber
                  }}<span v-if="option.customer.streetNumberSuffix">{{
                    option.customer.streetNumberSuffix
                  }}</span
                  >, {{ option.customer.zip }}
                  {{ option.customer.city }}
                </div>
              </template>
              <template slot="option" slot-scope="{ option }">
                <div>
                  #{{ option.number.toString().padStart(4, '0') }}: {{ option.customer.lastname }},
                  {{ option.customer.firstname }}
                </div>
                <div>
                  {{ option.customer.street }} {{ option.customer.streetNumber }},
                  {{ option.customer.streetNumberSuffix }}, {{ option.customer.zip }}
                  {{ option.customer.city }}
                </div>
              </template>
            </multiselect>
          </b-form-group>
        </b-col>

        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>{{ $t('additionalInfo') }}</strong>
          </legend>

          <b-form-group
            horizontal
            :label="$t('notes')"
            label-for="notes"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-textarea v-model="localInvoice.notes" :placeholder="$t('notes')" rows="3" />
          </b-form-group>

          <b-form-group
            v-if="localInvoice.number && localInvoice.number !== '_new'"
            horizontal
            :label="$t('number')"
            label-for="number"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            class="mb-0"
          >
            <div class="form-control-plaintext">
              {{ localInvoice.number.toString().padStart(4, '0') }}
            </div>
          </b-form-group>

          <b-form-group
            v-if="localInvoice.createdBy"
            horizontal
            :label="$t('createdBy')"
            label-for="createdBy"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            class="mb-0"
          >
            <div class="form-control-plaintext">
              {{ localInvoice.createdBy.firstname }} {{ localInvoice.createdBy.lastname
              }}<span v-if="localInvoice.createdAt"
                >, {{ $dayjs(localInvoice.createdAt).format('DD.MM.YYYY HH:mm') }}</span
              >
            </div>
          </b-form-group>

          <b-form-group
            v-if="localInvoice.updatedBy"
            horizontal
            :label="$t('updatedBy')"
            label-for="updatedBy"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            class="mb-0"
          >
            <div class="form-control-plaintext">
              {{ localInvoice.updatedBy.firstname }} {{ localInvoice.updatedBy.lastname
              }}<span v-if="localInvoice.updatedAt"
                >, {{ $dayjs(localInvoice.updatedAt).format('DD.MM.YYYY HH:mm') }}</span
              >
            </div>
          </b-form-group>
        </b-col>
      </b-row>
    </fieldset>

    <!-- Attachments Section -->
    <b-row>
      <b-col lg="6" md="6" sm="12">
        <AttachmentsSection
          :parentId="localInvoice._id"
          :attachments="localInvoice.attachments"
          :attachmentTypes="attachmentTypes"
          :isReadOnly="isReadOnly"
          :uploadCallback="uploadAttachment"
          uploadActionType="uploadInvoiceAttachment"
          @delete-attachment="onDeleteAttachment"
        />
      </b-col>
    </b-row>

    <!-- Form Actions (when in project context) -->
    <b-row v-if="projectContext">
      <b-col lg="12" md="12" sm="12">
        <div class="form-actions d-flex justify-content-end mt-3">
          <button v-if="showCancelButton" class="btn btn-default mr-2" @click="$emit('cancel')">
            {{ $t('cancel') }}
          </button>
          <button
            v-if="!isReadOnly"
            class="btn btn-inverse"
            @click="handleSave"
            :disabled="!isFormValid"
          >
            {{ localInvoice.number === '_new' ? $t('create') : $t('save') }}
          </button>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import Calendar from 'primevue/calendar';
import { getInvoiceStateColor, getInvoiceTypeColor } from '@/helpers/colors';
import { calculateTaxAmounts } from '@/helpers/taxAmounts';
import { FilterMatchMode } from 'primevue/api';
import AttachmentsSection from '@/components/AttachmentsSection.vue';
import MultiselectSuppliers from '@/components/MultiselectSuppliers.vue';
import TaxAmountFields from '@/components/Accounting/TaxAmountFields.vue';

export default {
  name: 'InvoiceForm',
  components: {
    Multiselect,
    Calendar,
    AttachmentsSection,
    MultiselectSuppliers,
    TaxAmountFields,
  },
  props: {
    invoice: {
      type: Object,
      required: true,
    },
    projectContext: {
      type: Object,
      default: null,
    },
    /** Number of the project of the block project-invoices; null in the view invoices. */
    projectNumber: {
      type: Number,
      default: null,
    },
    showCancelButton: {
      type: Boolean,
      default: false,
    },
    /** Viewer on the view invoices: shows the invoice and its files, loads no selection lists. */
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localInvoice: { ...this.invoice },
      filteredProjects: [],
      projectsFilter: {
        pagination: {
          page: 0,
          rowsPerPage: 50,
        },
        sortField: 'number',
        sortOrder: -1,
        filterName: 'invoices-project-filters',
        filters: {
          number: { value: null, matchMode: FilterMatchMode.EQUALS },
          customerLastname: { value: null, matchMode: FilterMatchMode.CONTAINS },
          customerFirstname: { value: null, matchMode: FilterMatchMode.CONTAINS },
          projectCustomerSearch: { value: null },
        },
      },
    };
  },
  computed: {
    ...mapGetters(['getEnumValues', 'getProjects']),
    ...mapGetters('auth', [
      'isAdmin',
      'isClient',
      'getCurrentUser',
      'isOrganizationRoleNavigationEnabled',
    ]),

    invoiceDate: {
      get() {
        return this.localInvoice.invoiceDate
          ? this.$dayjs(this.localInvoice.invoiceDate).toDate()
          : null;
      },
      set(value) {
        this.localInvoice.invoiceDate = value;
        this.emitUpdate();
      },
    },

    dueDate: {
      get() {
        return this.localInvoice.dueDate ? this.$dayjs(this.localInvoice.dueDate).toDate() : null;
      },
      set(value) {
        this.localInvoice.dueDate = value;
        this.emitUpdate();
      },
    },

    paidDate: {
      get() {
        return this.localInvoice.paidDate ? this.$dayjs(this.localInvoice.paidDate).toDate() : null;
      },
      set(value) {
        this.localInvoice.paidDate = value;
        this.emitUpdate();
      },
    },

    invoiceAmounts() {
      const { netAmount, vatPercentage, taxAmount, grossAmount } = this.localInvoice;
      return { netAmount, vatPercentage, taxAmount, grossAmount };
    },

    isFormValid() {
      return true;
    },

    isNew() {
      return this.localInvoice.number === '_new';
    },

    attachmentTypes() {
      // Invoice attachment types - can be extended later
      return ['INVOICE_DOCUMENT'];
    },
  },
  watch: {
    invoice: {
      handler(newInvoice) {
        console.log('InvoiceForm: invoice changed', newInvoice);
        this.localInvoice = { ...newInvoice };
        this.seedGrossOnlyLegacyAmounts();
      },
      deep: true,
      immediate: true,
    },
    localInvoice: {
      handler() {
        this.emitUpdate();
      },
      deep: true,
    },
    projectContext: {
      handler(newProject) {
        if (newProject) {
          this.localInvoice.project = newProject;
          this.emitUpdate();
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions([
      'fetchProjectsPaginated',
      'fetchProjectsPaginatedByClient',
      'fetchProjectSelection',
      'uploadInvoiceAttachment',
      'deleteInvoiceAttachment',
      'fetchEnumValues',
    ]),

    getInvoiceStateColor,
    getInvoiceTypeColor,

    /** The gross-only rule applies to invoices only; otherwise the shared calculation. */
    calculateInvoiceAmounts(amounts) {
      if (this.isGrossOnlyLegacyInvoice(amounts)) {
        return { netAmount: amounts.grossAmount, taxAmount: 0, grossAmount: amounts.grossAmount };
      }
      return calculateTaxAmounts(amounts);
    },

    onAmountsChange(amounts) {
      this.localInvoice = { ...this.localInvoice, ...amounts };
    },

    seedGrossOnlyLegacyAmounts() {
      if (!this.isGrossOnlyLegacyInvoice(this.localInvoice)) {
        return false;
      }

      this.localInvoice.netAmount = this.localInvoice.grossAmount;
      this.localInvoice.taxAmount = 0;
      return true;
    },

    isGrossOnlyLegacyInvoice(invoice) {
      if (invoice.vatPercentage != null) {
        return false;
      }

      const netAmount = invoice.netAmount ?? 0;
      const taxAmount = invoice.taxAmount ?? 0;
      const grossAmount = invoice.grossAmount ?? 0;

      return netAmount === 0 && taxAmount === 0 && grossAmount > 0;
    },

    emitUpdate() {
      this.$emit('update:invoice', this.localInvoice);
    },

    handleSave() {
      this.$emit('save', this.localInvoice);
    },

    async onProjectSearch(rawQuery) {
      const query = rawQuery.toLowerCase();
      this.projectsFilter.filters.projectCustomerSearch.value = query;
      await this.loadProjectData();
    },

    async loadProjectData() {
      this.filteredProjects = this.isOrganizationRoleNavigationEnabled
        ? await this.loadProjectSelection()
        : await this.loadProjectsByRole();

      // Ensure current project is in the list
      if (this.localInvoice && this.localInvoice.project) {
        const projectExists = this.filteredProjects.find(
          (p) => p.number === this.localInvoice.project.number
        );
        if (!projectExists) {
          this.filteredProjects.unshift(this.localInvoice.project);
        }
      }
    },

    /** Matrix-Authz: the level decides, not the old role field (see feature page rechnungen). */
    async loadProjectSelection() {
      return this.fetchProjectSelection({
        search: this.projectsFilter.filters.projectCustomerSearch.value,
        pagination: {
          skip: this.projectsFilter.pagination.page * this.projectsFilter.pagination.rowsPerPage,
          pageSize: this.projectsFilter.pagination.rowsPerPage,
        },
      });
    },

    /** Legacy-Authz: project list by the old role field, as before. */
    async loadProjectsByRole() {
      if (this.isClient) {
        await this.fetchProjectsPaginatedByClient({
          clientId: this.getCurrentUser.client.id,
          page: this.projectsFilter.pagination.page,
          pageSize: this.projectsFilter.pagination.rowsPerPage,
          sortField: this.projectsFilter.sortField,
          sortOrder: this.projectsFilter.sortOrder,
          filters: { ...this.projectsFilter.filters },
        });
      } else if (this.isAdmin) {
        await this.fetchProjectsPaginated({
          page: this.projectsFilter.pagination.page,
          pageSize: this.projectsFilter.pagination.rowsPerPage,
          sortField: this.projectsFilter.sortField,
          sortOrder: this.projectsFilter.sortOrder,
          filters: { ...this.projectsFilter.filters },
        });
      }
      return this.getProjects;
    },

    /** Upload of AttachmentsSection; in the block through the project's invoice. */
    uploadAttachment(upload) {
      return this.uploadInvoiceAttachment({
        ...upload,
        invoiceNumber: this.localInvoice.number,
        projectNumber: this.projectNumber,
      });
    },

    async onDeleteAttachment(attachmentId) {
      await this.deleteInvoiceAttachment({
        invoiceId: this.localInvoice._id,
        invoiceNumber: this.localInvoice.number,
        attachmentId,
        projectNumber: this.projectNumber,
      });
    },
  },

  async mounted() {
    await this.fetchEnumValues('InvoiceType');
    await this.fetchEnumValues('InvoiceState');
    // selection lists are needed for editing only
    if (!this.isReadOnly && !this.projectContext) {
      await this.loadProjectData();
    }
  },
};
</script>

<style scoped lang="scss">
.p-calendar {
  width: 100%;
}

.invoice-fieldset {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.form-actions {
  border-top: 1px solid #dee2e6;
  padding-top: 1rem;
  margin-top: 1rem;
}
</style>
